// Global Instructions Rule Applied!
//
// New Fund form — the create mode of the funds full form (2026-09-09). The
// Terms tab's own sections (Details, Status, Key Dates, Fundraising, Manager),
// built from the SAME field configs the Terms tab auto-patches, rendered in
// edit mode and committed by a single POST /funds. Commitments and fee terms
// stay on the Terms tab: they are configured after the fund exists.
// On success the "new" tab morphs in place into the edit tab (the
// journal-entries precedent: no remove+re-add, no screen flash) and the tabbed
// fund form takes over with the record already seeded in activeRecords.
//
// Admin-only. The ribbon button is hidden for non-admins and POST /funds
// returns 403, so a non-admin who lands here by URL sees the same shell the
// admin-only Configuration sections show.

import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Col, Row, Typography } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../../core/utils/utilities.ts'
import { isRequiredFieldValid } from '../../../../core/components/InputManager/utils/formatters.ts'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { transformFormTab } from '../../../../store/slices/tabSlice'
import { store } from '../../../../state_manager/store.ts'
import type { RootState } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { FIELD_KEY_SEPARATOR, TABLE_FUNDS } from '../../../../state_manager/globals.ts'
import type { FundTermsRecord } from '../../../../state_manager/data_models/fund_terms_record.ts'
import { FundRecordFields } from '../../../../state_manager/data_models/fund_record.ts'
import { setActiveRecord } from '../../../../state_manager/state_update_actions/active_records/index.ts'
import { addGlobalConfigFund } from '../../../../state_manager/state_update_actions/global_configs/index.ts'
import { createFund } from '../../services/fundsService.ts'
import { useFundTermsFieldOptions } from '../../hooks/useFundTermsFieldOptions.ts'
import {
  getDetailsFields,
  getFundraisingFields,
  getKeyDatesFields,
  getManagerFields,
  getStatusFields
} from '../../utils/fundTermsFieldConfigs.ts'
import type { FieldConfig } from '../../../../core/components/InputManager/types.ts'

const { Title, Text } = Typography

/** Tab key useTabManager assigns to `/funds/forms/full/new`. */
const NEW_FUND_TAB_KEY = `${TABLE_FUNDS}-form-new-new`
const FUNDS_KEY_PREFIX = `${TABLE_FUNDS}${FIELD_KEY_SEPARATOR}`

type FundDraft = Partial<FundTermsRecord>

export default function NewFundForm() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { closeFormTab } = useTabManagerContext()
  const isAppAdmin = useSelector((state: RootState) => state.globalConfigs?.is_app_admin === true)
  const { fieldOptions, fundStatuses } = useFundTermsFieldOptions(null)

  // managed_by_us defaults true on the column, is_sbic false; mirror both so
  // the switches and the row agree when the user never touches them.
  const [draft, setDraft] = useState<FundDraft>({ funds__managed_by_us: true, funds__is_sbic: false })
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)

  // One FormSection per entry, in Terms-tab order. Details spans the row;
  // the rest sit two-up.
  const sections = useMemo(
    () =>
      [
        { key: 'details', title: 'Details', color: 'blue', full: true, fields: getDetailsFields(draft, fieldOptions) },
        { key: 'status', title: 'Status', color: 'blue', full: false, fields: getStatusFields(draft, fieldOptions) },
        { key: 'key-dates', title: 'Key Dates', color: 'blue', full: false, fields: getKeyDatesFields(draft) },
        { key: 'fundraising', title: 'Fundraising', color: 'blue', full: false, fields: getFundraisingFields(draft, fieldOptions) },
        { key: 'manager', title: 'Manager', color: 'purple', full: false, fields: getManagerFields(draft, fieldOptions) }
      ] as const,
    [draft, fieldOptions]
  )
  const fields = useMemo<FieldConfig[]>(() => sections.flatMap((s) => s.fields), [sections])

  const handleChange = useCallback((key: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setDirty(true)
    setValidationErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleCancel = useCallback(() => {
    closeFormTab(NEW_FUND_TAB_KEY)
  }, [closeFormTab])

  const handleCreate = useCallback(async () => {
    // Required-field gate: per-field red text + one toast naming the fields,
    // and the InputManager scrolls/focuses the first bad field.
    const errors: Record<string, string> = {}
    fields.forEach((field) => {
      if (field.required && !isRequiredFieldValid((draft as Record<string, unknown>)[field.key])) {
        errors[field.key] = `${field.label || field.key} is required`
      }
    })
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      setValidationErrorsVersion((v) => v + 1)
      showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields')
      return
    }

    setSaving(true)
    try {
      // POST /funds takes bare column names; empty values are left to the
      // column defaults rather than sent as nulls.
      const payload: Record<string, unknown> = {}
      Object.entries(draft).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return
        payload[key.startsWith(FUNDS_KEY_PREFIX) ? key.slice(FUNDS_KEY_PREFIX.length) : key] = value
      })
      const token = getAccessToken(store.getState().auth)
      const res = await createFund(payload, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to create fund')
        return
      }
      const detail = res.data
      const fundId = String(detail[FundRecordFields.funds__id] ?? '')
      if (!fundId) {
        showErrorToast('The fund was created but its id did not come back — refresh the Funds list.')
        return
      }
      const name = String(detail[FundRecordFields.funds__name] ?? draft.funds__name ?? '')

      // Seed the edit tab's read chain (activeRecords first) and every fund
      // picker (globalConfigs.funds, lean get_global_configs_v3 shape; a new
      // fund is unclassified, so both status flags fail open exactly as the
      // RPC computes them).
      dispatch(setActiveRecord({ uuid: fundId, record: detail }))
      // is_active / is_fundraising_open exactly as get_global_configs_v3
      // computes them — fail-open on an unclassified clock.
      const frRow = fundStatuses.find((s: any) => s.id === draft.funds__fundraising_status_id)
      const invRow = fundStatuses.find((s: any) => s.id === draft.funds__investment_status_id)
      const optionalIds: Record<string, string> = {}
      ;(['functional_currency_id', 'pipeline_origination', 'fundraising_status_id', 'investment_status_id'] as const).forEach(
        (col) => {
          const v = (draft as Record<string, unknown>)[`${FUNDS_KEY_PREFIX}${col}`]
          if (typeof v === 'string' && v) optionalIds[col] = v
        }
      )
      dispatch(
        addGlobalConfigFund({
          id: fundId,
          name,
          legal_name: draft.funds__legal_name ?? null,
          ...optionalIds,
          managed_by_us: draft.funds__managed_by_us ?? true,
          is_sbic: draft.funds__is_sbic ?? false,
          is_active: !(frRow?.is_terminal === true) && !(invRow?.is_terminal === true),
          is_fundraising_open: frRow ? !!frRow.is_open : true
        })
      )
      toast.success('Fund created')
      setDirty(false)

      // Morph the "new" tab into the edit tab in place; the pane remounts on
      // the new key and the tabbed form takes over.
      dispatch(transformFormTab({ key: NEW_FUND_TAB_KEY, module: TABLE_FUNDS, recordId: fundId, title: name }))
      navigate(`/${TABLE_FUNDS}/forms/full/${fundId}`, { replace: true })
    } finally {
      setSaving(false)
    }
  }, [draft, fields, fundStatuses, dispatch, navigate])

  if (!isAppAdmin) {
    return (
      <div className='px-4 py-6'>
        <Title level={4} style={{ marginTop: 0 }}>New Fund</Title>
        <Text type='secondary'>Creating a fund requires administrator access.</Text>
      </div>
    )
  }

  return (
    <div className='h-full overflow-y-auto p-4'>
      <div className='flex items-center justify-between mb-3'>
        <Title level={4} className='!mb-0'>
          New Fund
        </Title>
        <div className='flex items-center gap-2'>
          {/* Owner spec: primary rightmost; Cancel only while dirty. */}
          {dirty ? <Button onClick={handleCancel}>Cancel</Button> : null}
          <Button type='primary' loading={saving} onClick={() => void handleCreate()}>
            Create
          </Button>
        </div>
      </div>

      <Row gutter={[12, 12]}>
        {sections.map((section) => (
          <Col key={section.key} xs={24} xl={section.full ? 24 : 12}>
            <FormSection title={section.title} titleColor={section.color} scrollable={false}>
              <InputManager
                inputMode='edit'
                labelPosition='above'
                fields={section.fields}
                onChange={handleChange}
                validationErrors={validationErrors}
                validationErrorsVersion={validationErrorsVersion}
              />
            </FormSection>
          </Col>
        ))}
      </Row>
    </div>
  )
}
